import React from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import LocalePicker from "@/components/layout/LocalePicker";
import { titleFor } from "@/components/layout/RouteTitle";
import { useSettings } from "@/context/SettingsContext";
import { pick } from "@/lib/languages";
import { appShellL } from "@/i18n/appShell";
import { pageTitlesL } from "@/i18n/common";
import AppBar from "./AppBar";
import { useAppBarTitleFor } from "./AppBarSlot";

// The tabs' own screens carry a large title and no back: the tab bar leads home.
const TAB_TITLES = { "/bookings": "trips", "/messages": "messages", "/account": "profile" };

/**
 * The website header's place in the traveller app (owner, 2026-10-24): on
 * Accueil one compact row, the logo and the language and currency chip (no
 * account, which is the Profil tab, no bell); a tab's large title on its own
 * screen; back and the page's title everywhere else. The services row and
 * the menu are the tab bar's job.
 */
// A screen that draws its own top: a results page with a search (the search
// in a pill), a stay's page (back and share over its photos), a flight ("Votre vol" and share),
// the confirmation (its green mark under the status bar), a booking's
// conversation (the partner's picture and name).
const ownTop = (path, search) => (path === "/hotels" && /(^|&)(city|check_in|check_out)=/.test(search))
  || (path === "/flights" && /(^|&)(origin_city|destination_city|date)=/.test(search))
  || (path === "/cars" && /(^|&)(city|check_in|check_out)=/.test(search))
  || /^\/(hotels|flights)\/[^/]+$/.test(path)
  || (/^\/cars\/[^/]+$/.test(path) && ["", "offer"].includes(new URLSearchParams(search).get("step") || ""))
  || /^\/booking\/[^/]+\/success$/.test(path)
  || /^\/messages\/booking\/[^/]+$/.test(path)
  || path === "/account";

export default function AppHeader() {
  const { pathname, search } = useLocation();
  const { lang, siteLang = lang } = useSettings();
  const named = useAppBarTitleFor(pathname);
  if (pathname === "/") {
    return (
      <header className="tk-appbar" data-variant="home" data-testid="site-header">
        <Link to="/" className="flex min-h-11 items-center" data-testid="logo-link">
          <Logo size={30} wordmarkClass="tk-app-wordmark text-touki-black" />
        </Link>
        <span className="flex-1" />
        <LocalePicker variant="compact" className="tk-app-locale" />
      </header>
    );
  }
  const path = pathname.replace(/\/+$/, "");
  if (ownTop(path, search.replace(/^\?/, ""))) return null;
  const tab = TAB_TITLES[path];
  if (tab) return <AppBar variant="tab" as="h1" title={pick(appShellL, lang).titles[tab]} />;
  if (path === "/account/details") return <AppBar title={pick(appShellL, lang).titles.account} fallback="/account" />;
  // A booking opened from a notification, with nothing behind it, goes back to the trips.
  return <AppBar title={named || titleFor(pathname, pick(pageTitlesL, siteLang))} fallback={/^\/booking\//.test(path) ? "/bookings" : "/"} />;
}
